import { DataSource } from '@angular/cdk/collections';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { BaseCrudService } from '../services/base-crud.service';
import { FilterParams } from '../models/filter-params';
import { Identifiable } from '@t-llevo/util';
import { toSignal } from '@angular/core/rxjs-interop';

export class ResultDataSource<T extends Identifiable, R>
  implements DataSource<T>
{
  readonly PAGE_SIZE = 10;
  readonly PAGE_SIZE_OPTIONS = [5, 10, 50, 100];
  private _filterParams: FilterParams = {};

  public get filterParams(): FilterParams {
    return this._filterParams;
  }
  public set filterParams(filters: FilterParams) {
    this._filterParams = { ...filters };
  }

  private loadingSubject = new BehaviorSubject<boolean>(false);
  private countSubject = new Subject<number>();
  private dataSubject = new BehaviorSubject<readonly T[]>([]);
  private stop$ = new Subject<boolean>();
  public loading$ = this.loadingSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  public loadingSignal = toSignal(this.loading$, { initialValue: false });
  public total$ = this.countSubject.asObservable();
  public totalSignal = toSignal(this.total$, { initialValue: 0 });

  constructor(private srv: BaseCrudService<T, R>) {}

  connect(): Observable<readonly T[]> {
    return this.srv.itemList$.pipe(
      tap((rsp) => {
        if (rsp) {
          this.countSubject.next(rsp.count);
        } else {
          this.countSubject.next(0);
        }
      }),
      map((rsp) => rsp.results)
    );
  }

  disconnect(): void {
    this.loadingSubject.complete();
    this.dataSubject.complete();
    this.countSubject.complete();
    this.stop$.next(true);
    this.stop$.complete();
  }

  loadData(url: string) {
    this.loadingSubject.next(true);
    this.srv
      .getItemList(url, this._filterParams)
      .pipe(
        takeUntil(this.stop$),
        catchError(() => of(null)),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe();
  }
}
